"use client";

import type { ReactNode, RefObject } from "react";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import {
  linearScale,
  niceTicks,
  type Ticks,
} from "@/lib/tool-kit/stats-clinical/ticks";

/*
 * A self-contained SVG chart frame for the clinical-statistics widgets: a white page,
 * light grid, axes with round tick values and axis titles. Colours are plain hex
 * attributes — not Tailwind classes or CSS variables — so the exported SVG and the PNG
 * rasterised from it look the same as the page. Titles arrive through props.
 */

export const CHART_INK = "#1f2937";
export const CHART_MUTED = "#6b7280";
export const CHART_GRID = "#e5e7eb";
export const CHART_BLUE = "#2563eb";
export const CHART_RED = "#dc2626";
export const CHART_GREEN = "#15803d";
export const CHART_AMBER = "#b45309";
const FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

export interface ChartArea {
  /** Data → pixel for the plotting rectangle. */
  x: (value: number) => number;
  y: (value: number) => number;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export function Chart({
  svgRef,
  idPrefix,
  label,
  width = 640,
  height = 420,
  rightMargin = 16,
  xDomain,
  yDomain,
  xLabel,
  yLabel,
  formatX,
  formatY,
  xTicks,
  yTicks,
  children,
}: {
  svgRef?: RefObject<SVGSVGElement | null>;
  /** Prefix for the SVG's clip-path id, unique on the page. */
  idPrefix: string;
  /** The accessible name of the chart. */
  label: string;
  width?: number;
  height?: number;
  rightMargin?: number;
  xDomain: readonly [number, number];
  yDomain: readonly [number, number];
  xLabel: string;
  yLabel: string;
  formatX: (value: number) => string;
  formatY: (value: number) => string;
  /** Fixed tick sets (a 0–1 axis, say); otherwise round values are chosen. */
  xTicks?: Ticks;
  yTicks?: Ticks;
  children: (area: ChartArea) => ReactNode;
}) {
  const left = 66;
  const top = 14;
  const right = width - rightMargin;
  const bottom = height - 54;
  const xAxis = xTicks ?? niceTicks(xDomain[0], xDomain[1]);
  const yAxis = yTicks ?? niceTicks(yDomain[0], yDomain[1]);
  const x = linearScale([xAxis.min, xAxis.max], [left, right]);
  const y = linearScale([yAxis.min, yAxis.max], [bottom, top]);
  const clipId = `${idPrefix}-clip`;
  return (
    <svg
      aria-label={label}
      className="h-auto w-full max-w-full rounded-lg border border-border"
      height={height}
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
    >
      <title>{label}</title>
      <rect fill="#ffffff" height={height} width={width} x={0} y={0} />
      <defs>
        <clipPath id={clipId}>
          <rect
            height={bottom - top + 8}
            width={right - left + 8}
            x={left - 4}
            y={top - 4}
          />
        </clipPath>
      </defs>
      {yAxis.values.map((value) => (
        <g key={`y${value}`}>
          <line
            stroke={CHART_GRID}
            x1={left}
            x2={right}
            y1={y(value)}
            y2={y(value)}
          />
          <text
            fill={CHART_MUTED}
            fontFamily={FONT}
            fontSize={11}
            textAnchor="end"
            x={left - 8}
            y={y(value) + 4}
          >
            {formatY(value)}
          </text>
        </g>
      ))}
      {xAxis.values.map((value) => (
        <g key={`x${value}`}>
          <line
            stroke={CHART_GRID}
            x1={x(value)}
            x2={x(value)}
            y1={top}
            y2={bottom}
          />
          <text
            fill={CHART_MUTED}
            fontFamily={FONT}
            fontSize={11}
            textAnchor="middle"
            x={x(value)}
            y={bottom + 18}
          >
            {formatX(value)}
          </text>
        </g>
      ))}
      <line stroke={CHART_MUTED} x1={left} x2={right} y1={bottom} y2={bottom} />
      <line stroke={CHART_MUTED} x1={left} x2={left} y1={top} y2={bottom} />
      <g clipPath={`url(#${clipId})`}>
        {children({ x, y, left, right, top, bottom })}
      </g>
      <text
        fill={CHART_INK}
        fontFamily={FONT}
        fontSize={12}
        textAnchor="middle"
        x={(left + right) / 2}
        y={height - 12}
      >
        {xLabel}
      </text>
      <text
        fill={CHART_INK}
        fontFamily={FONT}
        fontSize={12}
        textAnchor="middle"
        transform={`translate(16 ${(top + bottom) / 2}) rotate(-90)`}
      >
        {yLabel}
      </text>
    </svg>
  );
}

/** Download buttons for an on-page chart: SVG and PNG. */
export function ChartDownloads({
  svgRef,
  baseName,
  svgLabel,
  pngLabel,
}: {
  svgRef: RefObject<SVGSVGElement | null>;
  baseName: string;
  svgLabel: string;
  pngLabel: string;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      <button
        className={BUTTON_SECONDARY}
        onClick={() => {
          if (svgRef.current) {
            downloadSvg(`${baseName}.svg`, svgRef.current);
          }
        }}
        type="button"
      >
        {svgLabel}
      </button>
      <button
        className={BUTTON_SECONDARY}
        onClick={() => {
          if (svgRef.current) {
            downloadPng(`${baseName}.png`, svgRef.current).catch(
              () => undefined
            );
          }
        }}
        type="button"
      >
        {pngLabel}
      </button>
    </div>
  );
}
